import React, { useState, useEffect } from 'react'
import { dataService } from '@/lib/dataService'
import { BookOpen, Search, ChevronRight, CheckCircle, Clock, Check } from 'lucide-react'

export const AdminScheduleStatus: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [teachers, setTeachers] = useState<any[]>([])
  const [assignments, setAssignments] = useState<any[]>([])
  const [subjects, setSubjects] = useState<any[]>([])
  const [classrooms, setClassrooms] = useState<any[]>([])
  const [submissions, setSubmissions] = useState<any[]>([])
  const [sessions, setSessions] = useState<any[]>([])
  const [academicYears, setAcademicYears] = useState<any[]>([])
  const [selectedYearId, setSelectedYearId] = useState<string>('')
  
  // Search & Modal State
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTeacherData, setSelectedTeacherData] = useState<any | null>(null)

  const loadData = async () => {
    setLoading(true)
    try {
      const [
        allProfiles,
        allAssignments,
        allSubjects,
        allClassrooms,
        allSubmissions,
        allSessions,
        terms
      ] = await Promise.all([
        dataService.getProfiles(),
        dataService.getTeachingAssignments(),
        dataService.getSubjects(),
        dataService.getClassrooms(),
        dataService.getScoreSubmissions(),
        dataService.getAssessmentSessions(),
        dataService.getAcademicYears()
      ])

      const approvedProfiles = allProfiles.filter((p: any) => p.status === 'approved')
      setTeachers(approvedProfiles)
      setAssignments(allAssignments)
      setSubjects(allSubjects)
      setClassrooms(allClassrooms)
      setSubmissions(allSubmissions)
      setSessions(allSessions)
      setAcademicYears(terms)

      const activeTerm = terms.find((t: any) => t.is_active)
      if (activeTerm) {
        setSelectedYearId(String(activeTerm.id))
      } else if (terms.length > 0) {
        setSelectedYearId(String(terms[0].id))
      }
    } catch (err) {
      console.error('Error loading schedule status:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const getSubjectInfo = (id: number) => {
    const s = subjects.find(sub => sub.id === id)
    return s ? `${s.code} ${s.name}` : 'ไม่ทราบวิชา'
  }

  const getClassroomName = (id: number) => {
    const c = classrooms.find(cl => cl.id === id)
    return c ? `ม.${c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level}/${c.room_number}` : 'ไม่ทราบห้อง'
  }

  const getSubmissionStatus = (assignmentId: number, sessionId: number) => {
    const sub = submissions.find(s => s.teaching_assignment_id === assignmentId && s.assessment_session_id === sessionId)
    if (!sub) return { pre: 'draft', post: 'draft' }
    return { pre: sub.pre_test_status, post: sub.post_test_status }
  }

  // Filter assignments by active year
  const activeYearId = Number(selectedYearId)
  const currentSession = sessions.find(s => s.academic_year_id === activeYearId && s.is_open) || sessions[0]

  const yearAssignments = assignments.filter(a => a.academic_year_id === activeYearId)

  // Group assignments by teacher
  const groupedTeachers = teachers.map(teacher => {
    const teacherAssignments = yearAssignments.filter(a => a.teacher_id === teacher.id)
    
    let preCompleted = 0
    let postCompleted = 0
    
    teacherAssignments.forEach(a => {
      if (currentSession) {
        const status = getSubmissionStatus(a.id, currentSession.id)
        if (status.pre === 'submitted') preCompleted++
        if (status.post === 'submitted') postCompleted++
      }
    })

    const totalExpected = teacherAssignments.length
    const prePercent = totalExpected > 0 ? Math.round((preCompleted / totalExpected) * 100) : 0
    const postPercent = totalExpected > 0 ? Math.round((postCompleted / totalExpected) * 100) : 0
    const allDone = totalExpected > 0 && preCompleted === totalExpected && postCompleted === totalExpected

    return {
      teacher,
      assignments: teacherAssignments,
      preCompleted,
      postCompleted,
      prePercent,
      postPercent,
      totalExpected,
      allDone
    }
  }).filter(g => {
    // Filter out teachers who have no assignments in this academic year
    if (g.assignments.length === 0) return false

    // Search query filter
    const query = searchQuery.trim().toLowerCase()
    if (!query) return true
    const name = `${g.teacher.first_name} ${g.teacher.last_name}`.toLowerCase()
    const subjectsList = g.assignments.map(a => getSubjectInfo(a.subject_id).toLowerCase()).join(' ')
    const roomsList = g.assignments.map(a => getClassroomName(a.classroom_id).toLowerCase()).join(' ')
    
    return name.includes(query) || subjectsList.includes(query) || roomsList.includes(query)
  })

  // Calculate Overall Compliance KPI
  const totalActiveAssignments = yearAssignments.length
  let totalPreSubmitted = 0
  let totalPostSubmitted = 0
  yearAssignments.forEach(a => {
    if (currentSession) {
      const status = getSubmissionStatus(a.id, currentSession.id)
      if (status.pre === 'submitted') totalPreSubmitted++
      if (status.post === 'submitted') totalPostSubmitted++
    }
  })
  const overallPreRate = totalActiveAssignments > 0 ? Math.round((totalPreSubmitted / totalActiveAssignments) * 100) : 0
  const overallPostRate = totalActiveAssignments > 0 ? Math.round((totalPostSubmitted / totalActiveAssignments) * 100) : 0

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px', paddingBottom: '40px' }}>
      
      {/* Header and Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '26px', fontWeight: '800', margin: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '10px' }}>
            📅 สถานะส่งคะแนนแบ่งตามตารางสอน
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: '4px 0 0 0' }}>
            ติดตามการป้อนคะแนน Pre/Post-Test แยกตามรายวิชาที่สอนของครูแต่ละท่านสะสมประจำภาคเรียน
          </p>
        </div>

        {/* Year Filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)' }}>ปีการศึกษา:</span>
          <select
            value={selectedYearId}
            onChange={(e) => setSelectedYearId(e.target.value)}
            className="input-field"
            style={{ padding: '8px 16px', fontSize: '13px', borderRadius: '10px', width: '160px', background: 'var(--bg-card)' }}
          >
            {academicYears.map(term => (
              <option key={term.id} value={term.id}>
                {term.year} ภาคเรียนที่ {term.semester} {term.is_active ? '(ปัจจุบัน)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Compliance Overview KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px'
      }}>
        {/* Total Classrooms Card */}
        <div className="glassmorphism" style={{ padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(99, 102, 241, 0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <BookOpen size={22} />
          </div>
          <div>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', fontWeight: '600' }}>ตารางสอนที่ทำงานอยู่</span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)' }}>{totalActiveAssignments} รายการ</span>
          </div>
        </div>

        {/* Pre-Test compliance card */}
        <div className="glassmorphism" style={{ padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Clock size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', fontWeight: '600' }}>ภาพรวมส่ง Pre-Test</span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)' }}>{overallPreRate}%</span>
            <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${overallPreRate}%`, background: 'var(--primary)', borderRadius: '2px' }} />
            </div>
          </div>
        </div>

        {/* Post-Test compliance card */}
        <div className="glassmorphism" style={{ padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.1)', color: 'var(--success)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircle size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', fontWeight: '600' }}>ภาพรวมส่ง Post-Test</span>
            <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-primary)' }}>{overallPostRate}%</span>
            <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${overallPostRate}%`, background: 'var(--success)', borderRadius: '2px' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Filter controls */}
      <div className="glassmorphism" style={{ padding: '20px', borderRadius: '16px', border: '1px solid var(--border)', background: 'var(--bg-card)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        {/* Search */}
        <div style={{ position: 'relative', width: '100%', maxWidth: '380px' }}>
          <Search size={18} style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text"
            placeholder="ค้นหาชื่อครู, วิชาเรียน หรือห้องเรียน..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-field"
            style={{ padding: '10px 16px 10px 42px', fontSize: '13px', borderRadius: '10px', width: '100%' }}
          />
        </div>

        {/* Live Feedback Stats */}
        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span>🔍 แสดงข้อมูลครูผู้สอน</span>
          <span style={{ color: 'var(--primary)', background: 'rgba(99, 102, 241, 0.08)', padding: '4px 10px', borderRadius: '8px' }}>
            {groupedTeachers.length} จาก {teachers.length} คน
          </span>
        </div>
      </div>

      {/* Main Grid of Teacher Cards */}
      {groupedTeachers.length === 0 ? (
        <div className="glassmorphism" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          📭 ไม่พบตารางสอนตรงตามเงื่อนไขค้นหา
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: '24px'
        }}>
          {groupedTeachers.map((data) => {
            const { teacher, preCompleted, postCompleted, prePercent, postPercent, totalExpected, allDone } = data
            const tid = teacher.id
            const initials = teacher.first_name.substring(0, 1) + teacher.last_name.substring(0, 1)

            const roleLabel = teacher.role === 'head_of_department' ? 'หัวหน้ากลุ่มสาระ' : teacher.role === 'admin' ? 'ผู้ดูแลระบบ' : 'ครูผู้สอน'
            const roleColor = teacher.role === 'head_of_department' ? 'var(--secondary)' : teacher.role === 'admin' ? 'var(--primary)' : '#b45309'
            const roleBg = teacher.role === 'head_of_department' ? 'rgba(99, 102, 241, 0.08)' : teacher.role === 'admin' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)'

            return (
              <div 
                key={tid}
                onClick={() => setSelectedTeacherData(data)}
                className="glassmorphism hover-card-effect"
                style={{
                  borderRadius: '20px',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-card)',
                  display: 'flex',
                  flexDirection: 'column',
                  height: 'fit-content',
                  overflow: 'hidden',
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  paddingBottom: '16px'
                }}
              >
                {/* Status Indicator Bar */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: allDone ? 'linear-gradient(90deg, #10b981, #34d399)' : 'linear-gradient(90deg, #6366f1, #3b82f6)'
                }} />

                {/* Card Header Panel */}
                <div style={{ padding: '20px 20px 12px 20px', display: 'flex', gap: '14px', alignItems: 'center' }}>
                  {/* Initials circle */}
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: allDone ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                    color: allDone ? 'var(--success)' : 'var(--primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: '700',
                    fontSize: '15px',
                    border: `1px solid ${allDone ? 'rgba(16, 185, 129, 0.2)' : 'rgba(99, 102, 241, 0.2)'}`
                  }}>
                    {initials}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
                        {teacher.first_name} {teacher.last_name}
                      </span>
                      <span style={{
                        fontSize: '10px',
                        fontWeight: '700',
                        color: roleColor,
                        background: roleBg,
                        border: `1px solid ${roleColor}`,
                        padding: '1px 6px',
                        borderRadius: '6px'
                      }}>{roleLabel}</span>
                    </div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
                      📚 มอบหมายงานสอน {totalExpected} วิชาห้อง
                    </span>
                  </div>

                  <div style={{ color: 'var(--text-secondary)' }}>
                    <ChevronRight size={18} />
                  </div>
                </div>

                {/* Progress compliance summaries */}
                <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '600', marginBottom: '3px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Pre-Test: {preCompleted} / {totalExpected}</span>
                      <span style={{ color: 'var(--text-primary)' }}>{prePercent}%</span>
                    </div>
                    <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${prePercent}%`, background: 'var(--primary)', borderRadius: '2px' }} />
                    </div>
                  </div>

                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', fontWeight: '600', marginBottom: '3px' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Post-Test: {postCompleted} / {totalExpected}</span>
                      <span style={{ color: 'var(--text-primary)' }}>{postPercent}%</span>
                    </div>
                    <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${postPercent}%`, background: 'var(--success)', borderRadius: '2px' }} />
                    </div>
                  </div>
                </div>

                {/* Subtle Interactive Instruction */}
                <div style={{
                  marginTop: '14px',
                  paddingTop: '10px',
                  borderTop: '1px solid var(--border)',
                  marginRight: '20px',
                  marginLeft: '20px',
                  textAlign: 'center',
                  fontSize: '11px',
                  fontWeight: '700',
                  color: 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px'
                }}>
                  คลิกเพื่อดูรายละเอียดรายห้องเรียน ➔
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Premium Detail Modal Popup */}
      {selectedTeacherData && (
        <div 
          onClick={() => setSelectedTeacherData(null)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px'
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            className="glassmorphism"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              borderRadius: '24px',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: 'var(--shadow-lg)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                <div style={{
                  width: '46px',
                  height: '46px',
                  borderRadius: '12px',
                  background: selectedTeacherData.allDone ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)',
                  color: selectedTeacherData.allDone ? 'var(--success)' : 'var(--primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '15px',
                  border: `1px solid ${selectedTeacherData.allDone ? 'rgba(16, 185, 129, 0.2)' : 'rgba(99, 102, 241, 0.2)'}`
                }}>
                  {selectedTeacherData.teacher.first_name.substring(0, 1) + selectedTeacherData.teacher.last_name.substring(0, 1)}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-primary)' }}>
                      {selectedTeacherData.teacher.first_name} {selectedTeacherData.teacher.last_name}
                    </span>
                    <span style={{
                      fontSize: '10px',
                      fontWeight: '700',
                      color: selectedTeacherData.teacher.role === 'head_of_department' ? 'var(--secondary)' : selectedTeacherData.teacher.role === 'admin' ? 'var(--primary)' : '#b45309',
                      background: selectedTeacherData.teacher.role === 'head_of_department' ? 'rgba(99, 102, 241, 0.08)' : selectedTeacherData.teacher.role === 'admin' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                      border: `1px solid ${selectedTeacherData.teacher.role === 'head_of_department' ? 'var(--secondary)' : selectedTeacherData.teacher.role === 'admin' ? 'var(--primary)' : '#b45309'}`,
                      padding: '1px 6px',
                      borderRadius: '6px'
                    }}>{selectedTeacherData.teacher.role === 'head_of_department' ? 'หัวหน้ากลุ่มสาระ' : selectedTeacherData.teacher.role === 'admin' ? 'ผู้ดูแลระบบ' : 'ครูผู้สอน'}</span>
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginTop: '2px' }}>
                    📧 {selectedTeacherData.teacher.email}
                  </span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedTeacherData(null)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Progress Summary Section */}
              <div style={{ 
                background: 'rgba(0,0,0,0.01)',
                border: '1px solid var(--border)',
                borderRadius: '16px',
                padding: '16px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <span style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Pre-Test Compliance</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>{selectedTeacherData.prePercent}%</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>({selectedTeacherData.preCompleted}/{selectedTeacherData.totalExpected} ห้อง)</span>
                  </div>
                  <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${selectedTeacherData.prePercent}%`, background: 'var(--primary)', borderRadius: '2px' }} />
                  </div>
                </div>

                <div>
                  <span style={{ display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-secondary)', marginBottom: '4px' }}>Post-Test Compliance</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-primary)' }}>{selectedTeacherData.postPercent}%</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>({selectedTeacherData.postCompleted}/{selectedTeacherData.totalExpected} ห้อง)</span>
                  </div>
                  <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${selectedTeacherData.postPercent}%`, background: 'var(--success)', borderRadius: '2px' }} />
                  </div>
                </div>
              </div>

              {/* Classrooms List */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)' }}>
                  รายวิชาและห้องเรียนที่ดูแล ({selectedTeacherData.totalExpected} รายการ)
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {selectedTeacherData.assignments.map((assign: any) => {
                    const status = currentSession ? getSubmissionStatus(assign.id, currentSession.id) : { pre: 'draft', post: 'draft' }
                    return (
                      <div 
                        key={assign.id}
                        style={{
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border)',
                          borderRadius: '14px',
                          padding: '12px 16px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '12px'
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 }}>
                          <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getSubjectInfo(assign.subject_id)}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            ห้องเรียน: {getClassroomName(assign.classroom_id)}
                          </span>
                        </div>

                        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                          {/* Pre Test */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: 'var(--text-secondary)' }}>Pre:</span>
                            {status.pre === 'submitted' ? (
                              <span style={{ fontSize: '10.5px', fontWeight: '700', color: 'var(--success)', background: 'rgba(16, 185, 129, 0.08)', padding: '2px 8px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                <Check size={10} strokeWidth={3} /> ส่งแล้ว
                              </span>
                            ) : (
                              <span style={{ fontSize: '10.5px', fontWeight: '700', color: 'var(--warning)', background: 'rgba(245, 158, 11, 0.08)', padding: '2px 8px', borderRadius: '6px' }}>
                                ⏳ รอดราฟต์
                              </span>
                            )}
                          </div>

                          {/* Post Test */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                            <span style={{ fontSize: '9px', fontWeight: '600', color: 'var(--text-secondary)' }}>Post:</span>
                            {status.post === 'submitted' ? (
                              <span style={{ fontSize: '10.5px', fontWeight: '700', color: 'var(--success)', background: 'rgba(16, 185, 129, 0.08)', padding: '2px 8px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                                <Check size={10} strokeWidth={3} /> ส่งแล้ว
                              </span>
                            ) : (
                              <span style={{ fontSize: '10.5px', fontWeight: '700', color: 'var(--warning)', background: 'rgba(245, 158, 11, 0.08)', padding: '2px 8px', borderRadius: '6px' }}>
                                ⏳ รอดราฟต์
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button 
                onClick={() => setSelectedTeacherData(null)}
                className="btn btn-primary"
                style={{ padding: '8px 20px', borderRadius: '10px', fontSize: '13px' }}
              >
                ตกลง
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
